import { useFeatures } from '@atc/features';

/**
 * Custom hook to consolidate and organize all feature flags for Search Results Page
 *
 * @returns {Object} Organized feature configuration
 * @property {Object} brandConfig - Brand-specific configuration (channel, vhrProviders, etc.)
 * @property {Object} adConfig - Ad placement and display configuration
 * @property {Object} spotlightConfig - Spotlight listings configuration
 * @property {Object} listingConfig - Individual listing display configuration
 * @property {Object} walletConfig - My Wallet feature configuration
 * @property {Object} supplementalConfig - Supplemental search configuration
 * @property {Object} infiniteScrollConfig - Infinite scroll and lazy loading configuration
 * @property {Object} boostConfig - New car boost configuration
 * @property {Object} tileConfig - Carfax/Experian tile configuration
 * @property {Object} derivedConfig - Computed flags from multiple sources
 */
export function useFeatureConfiguration() {
    const featureFlags = useFeatures([
        'brand',
        'default_query_params',
        'enable_new_car_boost',
        'fluid_ad',
        'listing_card_ad',
        'my_wallet',
        'non_relevant_sort_spotlights',
        'override_carfax_tile',
        'premium_spotlight',
        'remove_carfax_tile',
        'remove_experian_tile',
        'similar_listings_srp',
        'srp_bottom_lazy_threshold',
        'srp_inline_filters',
        'srp_liner_ad',
        'srp_wallet_grid_promo_card',
        'standard_listing_owner_name',
        'standard_listing_phone_display',
        'supplemental',
        'treehouse_ad',
        'vehicle_highlight',
    ]);

    // Brand configuration
    const [, brandSettings] = featureFlags.brand;
    const brandConfig = {
        channel: brandSettings.channel,
        vhrProviders: brandSettings.vhrProviders,
        relevanceConfig: brandSettings.relevanceConfig,
        inventoryImageUrl: brandSettings.inventory_image_url,
    };

    // Ad configuration
    const [enableFluidAd] = featureFlags.fluid_ad;
    const [enableListingCardAd] = featureFlags.listing_card_ad;
    const [, linerAdSettings] = featureFlags.srp_liner_ad;
    const [isTreehouseAdEnabled] = featureFlags.treehouse_ad;

    const adConfig = {
        enableFluidAd,
        enableListingCardAd,
        isTreehouseAdEnabled,
        linerAd: {
            hideLinerAd1: linerAdSettings.hide_liner_ad1,
            hideLinerAd2: linerAdSettings.hide_liner_ad2,
            hideLinerAd3: linerAdSettings.hide_liner_ad3,
            hideLinerAd4: linerAdSettings.hide_liner_ad4,
            hideLinerAd5: linerAdSettings.hide_liner_ad5,
        },
    };

    // Spotlight configuration
    const [enablePremiumSpotlight, premiumSpotlightSettings] = featureFlags.premium_spotlight;
    const [enableSpotlightNonRelevantSort, nonRelevantSpotlightSettings] = featureFlags.non_relevant_sort_spotlights;
    const [isVehicleHighlightEnabled] = featureFlags.vehicle_highlight;

    const spotlightConfig = {
        enablePremiumSpotlight,
        enableSpotlightNonRelevantSort,
        isVehicleHighlightEnabled,
        carouselRowPlacement: premiumSpotlightSettings.carousel_row_placement,
        peekabooListingPlacement: premiumSpotlightSettings.peekaboo_listing_placement,
        showSpotlightsBeforeMEE: nonRelevantSpotlightSettings.before_mee,
        showSpotlightsBottomPage: nonRelevantSpotlightSettings.bottom_page,
        hideSpotlights: nonRelevantSpotlightSettings.hide_spotlights,
        enableVehicleHighlights: enablePremiumSpotlight && isVehicleHighlightEnabled,
    };

    // Listing display configuration
    const [hideOwnerName] = featureFlags.standard_listing_owner_name;
    const [displayPhoneNumber] = featureFlags.standard_listing_phone_display;
    const [enableInlineFilters] = featureFlags.srp_inline_filters;

    const listingConfig = {
        hideOwnerName,
        displayPhoneNumber,
        enableInlineFilters,
    };

    // Tile configuration (Carfax/Experian)
    const [isOverrideCarfaxTileEnabled, carfaxTileSettings] = featureFlags.override_carfax_tile;
    const [removeCarfaxTile] = featureFlags.remove_carfax_tile;
    const [removeExperianTile] = featureFlags.remove_experian_tile;

    const tileConfig = {
        isOverrideCarfaxTileEnabled,
        partner: carfaxTileSettings.partner,
        removeCarfaxTile,
        removeExperianTile,
    };

    // My Wallet configuration
    const [enableMyWallet] = featureFlags.my_wallet;
    const [enableNewWalletPromoCard] = featureFlags.srp_wallet_grid_promo_card;

    const walletConfig = {
        enableMyWallet,
        enableNewWalletPromoCard,
    };

    // Supplemental search configuration
    const [supplementalEnabled] = featureFlags.supplemental;
    const [enableSimilarListings] = featureFlags.similar_listings_srp;

    const supplementalConfig = {
        supplementalEnabled,
        enableSimilarListings,
    };

    // Infinite scroll and lazy loading configuration
    const [, scrollThresholdSettings] = featureFlags.srp_bottom_lazy_threshold;
    const [enableDefaultQueryParams, queryParamsSettings] = featureFlags.default_query_params;

    const infiniteScrollConfig = {
        defaultThreshold: scrollThresholdSettings.defaultThreshold,
        enableDefaultQueryParams,
        queryParams: queryParamsSettings.queryParams,
    };

    // New car boost configuration
    const [enableNewCarBoost] = featureFlags.enable_new_car_boost;

    const boostConfig = {
        enableNewCarBoost,
    };

    return {
        brandConfig,
        adConfig,
        spotlightConfig,
        listingConfig,
        tileConfig,
        walletConfig,
        supplementalConfig,
        infiniteScrollConfig,
        boostConfig,
    };
}
